import { useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowRight, CheckCircle, Eye, EyeSlash } from "@phosphor-icons/react";
import { formatApiError } from "@/lib/api";
import VaultLogo from "@/components/VaultLogo";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export default function ResetPassword() {
  const { token } = useParams();
  const nav = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (password.length < 6) {
      setErr("Password must be at least 6 characters.");
      return;
    }
    if (password !== confirm) {
      setErr("Passwords don't match.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API_BASE}/api/auth/reset-password`, { token, password });
      setDone(true);
      setTimeout(() => nav("/login"), 2500);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen noise grid grid-cols-1 lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between p-12 bg-[var(--brand-ink)] text-white">
        <Link to="/" className="font-display text-2xl font-bold flex items-center gap-2" data-testid="reset-brand">
          <VaultLogo size={28} dark/>JobVault
        </Link>
        <div>
          <p className="label-tiny text-[var(--pastel-yellow)] mb-3">/new password</p>
          <h2 className="font-display text-5xl font-semibold leading-[1.05]">
            new password.<br/>
            <span className="text-[var(--pastel-yellow)]">same vault.</span>
          </h2>
          <p className="mt-8 text-white/70 max-w-md leading-relaxed">
            make it good. make it memorable. then never lose it again — or do, and come back here. no judgement.
          </p>
        </div>
        <p className="font-mono-d text-xs text-white/40">single-use link · expires in 1h</p>
      </div>

      <div className="flex items-center justify-center p-8 lg:p-16">
        <div className="w-full max-w-md">
          <p className="label-tiny text-[var(--brand-muted)] mb-2">/reset password</p>
          <h1 className="font-display text-4xl font-semibold tracking-tight mb-8">set a new password.</h1>

          {done ? (
            <div className="card-hard rounded-md p-6 bg-[var(--pastel-mint)]" data-testid="reset-success">
              <p className="label-tiny mb-2 flex items-center gap-1.5">
                <CheckCircle size={12} weight="duotone"/> /done
              </p>
              <h3 className="font-display text-2xl font-semibold tracking-tight">password updated.</h3>
              <p className="text-sm mt-3 text-[var(--brand-muted)]">redirecting you to login…</p>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="reset-form">
              {err && <div data-testid="reset-error" className="card-hard rounded-md p-3 bg-[var(--pastel-peach)] text-sm mb-4">{err}</div>}

              <label className="label-tiny block mb-2" htmlFor="reset-password">new password</label>
              <div className="relative mb-4">
                <input id="reset-password" data-testid="reset-password" name="new-password"
                  type={showPw ? "text" : "password"} required minLength={6}
                  autoComplete="new-password"
                  value={password} onChange={(e) => setPassword(e.target.value)}
                  placeholder="at least 6 characters"
                  className="w-full card-hard rounded-md px-4 py-3 pr-12 focus:ring-2 focus:ring-[var(--brand-primary)]"/>
                <button type="button" onClick={() => setShowPw((v) => !v)}
                  data-testid="reset-show-password"
                  aria-label={showPw ? "hide password" : "show password"}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded hover:bg-[var(--pastel-yellow)]">
                  {showPw ? <EyeSlash size={18}/> : <Eye size={18}/>}
                </button>
              </div>

              <label className="label-tiny block mb-2" htmlFor="reset-confirm">confirm new password</label>
              <input id="reset-confirm" data-testid="reset-confirm" name="confirm-password"
                type={showPw ? "text" : "password"} required
                autoComplete="new-password"
                value={confirm} onChange={(e) => setConfirm(e.target.value)}
                className="w-full card-hard rounded-md px-4 py-3 mb-6 focus:ring-2 focus:ring-[var(--brand-primary)]"/>

              <button data-testid="reset-submit" disabled={loading} type="submit"
                className="w-full btn-hard bg-[var(--brand-primary)] text-white rounded-md py-3.5 font-medium flex items-center justify-center gap-2 disabled:opacity-60">
                {loading ? "updating…" : <>set new password <ArrowRight weight="bold" size={16}/></>}
              </button>

              <p className="mt-6 text-sm text-[var(--brand-muted)]">
                changed your mind? <Link to="/login" data-testid="reset-to-login" className="text-[var(--brand-ink)] underline font-medium">back to login</Link>
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
